import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { lazy, Suspense, useEffect, type ComponentType } from "react";
import { Loader2 } from "lucide-react";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import AuthDebugOverlay from "@/components/auth/AuthDebugOverlay";
import LegacyRedirect from "@/components/routing/LegacyRedirect";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { trackPageView } from "@/lib/meta-pixel";
import TrackingScriptsInjector from "@/components/TrackingScriptsInjector";
import GrowthFunnelTracker from "@/components/GrowthFunnelTracker";

// Wraps React.lazy with a one-time reload on chunk fetch failure.
// Fixes "Failed to fetch dynamically imported module" after a new deploy
// invalidates the previously hashed chunk filenames.
function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const STORAGE_KEY = "lovable:chunk-reload";
    try {
      const mod = await factory();
      try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
      return mod;
    } catch (err: any) {
      const message = String(err?.message || "");
      const isChunkError =
        err?.name === "ChunkLoadError" ||
        /Failed to fetch dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message) ||
        /error loading dynamically imported module/i.test(message) ||
        /dynamically imported module/i.test(message);
      if (isChunkError && typeof window !== "undefined") {
        // 1) Try once more — transient network/CDN misses are common.
        try {
          await new Promise((r) => setTimeout(r, 600));
          const retried = await factory();
          try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
          return retried;
        } catch {}

        // 2) Stale index.html pointing to removed hashed chunks: hard reload once.
        let attempts = 0;
        try { attempts = Number(sessionStorage.getItem(STORAGE_KEY) || "0"); } catch {}
        if (attempts < 2) {
          try { sessionStorage.setItem(STORAGE_KEY, String(attempts + 1)); } catch {}
          // Bypass the HTTP cache so the fresh asset manifest is fetched.
          const url = new URL(window.location.href);
          url.searchParams.set("__reload", String(Date.now()));
          window.location.replace(url.toString());
          // Return a placeholder while the page reloads.
          return { default: (() => null) as unknown as T };
        }
      }
      throw err;
    }
  });
}

// Public blog pages — lazy loaded for code splitting
const PublicBlog = lazyWithRetry(() => import("./pages/PublicBlog"));
const PublicBlogPost = lazyWithRetry(() => import("./pages/PublicBlogPost"));
const PublicBlogMateriais = lazyWithRetry(() => import("./pages/PublicBlogMateriais"));
const PublicBlogCategory = lazyWithRetry(() => import("./pages/PublicBlogCategory"));
const HostedBlogSitemap = lazyWithRetry(() => import("./pages/HostedBlogSitemap"));
import CustomDomainRouter from "./pages/CustomDomainRouter";
import { isCustomDomain } from "./lib/blogDomainResolver";

// Lazy-loaded pages
const FastblogLanding = lazyWithRetry(() => import("./pages/FastblogLanding"));
const FastblogPlanos = lazyWithRetry(() => import("./pages/FastblogPlanos"));
const FastblogFuncionalidades = lazyWithRetry(() => import("./pages/FastblogFuncionalidades"));
const FastblogRecursos = lazyWithRetry(() => import("./pages/FastblogRecursos"));
const FastblogParcerias = lazyWithRetry(() => import("./pages/FastblogParcerias"));
const TermosServico = lazyWithRetry(() => import("./pages/TermosServico"));
const PoliticaPrivacidade = lazyWithRetry(() => import("./pages/PoliticaPrivacidade"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const OAuthConsent = lazyWithRetry(() => import("./pages/OAuthConsent"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const DashboardLayoutRoute = lazyWithRetry(() => import("./pages/DashboardLayoutRoute"));
const DashboardBlogAparencia = lazyWithRetry(() => import("./pages/DashboardBlogAparencia"));
const DashboardBlog = lazyWithRetry(() => import("./pages/DashboardBlog"));
const ArtigoView = lazyWithRetry(() => import("./pages/ArtigoView"));
const DashboardPlanejamento = lazyWithRetry(() => import("./pages/DashboardPlanejamento"));
const DashboardPesquisa = lazyWithRetry(() => import("./pages/DashboardPesquisa"));
const DashboardMinhaMarca = lazyWithRetry(() => import("./pages/DashboardMinhaMarca"));
const ComingSoon = lazyWithRetry(() => import("./pages/ComingSoon"));
const DashboardConfiguracoes = lazyWithRetry(() => import("./pages/DashboardConfiguracoes"));
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard"));
const AdminNovoCliente = lazyWithRetry(() => import("./pages/admin/AdminNovoCliente"));
const AdminClienteDetalhes = lazyWithRetry(() => import("./pages/admin/AdminClienteDetalhes"));
const AdminIntegracoes = lazyWithRetry(() => import("./pages/admin/AdminIntegracoes"));
const AdminConfiguracoes = lazyWithRetry(() => import("./pages/admin/AdminConfiguracoes"));
const AdminAgenteIA = lazyWithRetry(() => import("./pages/admin/AdminAgenteIA"));
const AdminUsoIA = lazyWithRetry(() => import("./pages/admin/AdminUsoIA"));
const AdminNotaFiscal = lazyWithRetry(() => import("./pages/admin/AdminNotaFiscal"));
const CheckoutBoleto = lazyWithRetry(() => import("./pages/CheckoutBoleto"));
const AdminCheckoutBoleto = lazyWithRetry(() => import("./pages/admin/AdminCheckoutBoleto"));
const AdminPlanos = lazyWithRetry(() => import("./pages/admin/AdminPlanos"));
const AdminDev = lazyWithRetry(() => import("./pages/admin/AdminDev"));
const AdminSaude = lazyWithRetry(() => import("./pages/admin/AdminSaude"));
const AdminAuditoria = lazyWithRetry(() => import("./pages/admin/AdminAuditoria"));
const AdminOperacao = lazyWithRetry(() => import("./pages/admin/AdminOperacao"));
const AdminTracking = lazyWithRetry(() => import("./pages/admin/AdminTracking"));
const AdminFuncoes = lazyWithRetry(() => import("./pages/admin/AdminFuncoes"));
const AdminFuncaoDetalhe = lazyWithRetry(() => import("./pages/admin/AdminFuncaoDetalhe"));
const AdminKnowledgeBase = lazyWithRetry(() => import("./pages/admin/AdminKnowledgeBase"));
const AdminMarketing = lazyWithRetry(() => import("./pages/admin/AdminMarketing"));
const AdminPerformance = lazyWithRetry(() => import("./pages/admin/AdminPerformance"));
const HelpCenter = lazyWithRetry(() => import("./pages/help/HelpCenter"));
const HelpArticle = lazyWithRetry(() => import("./pages/help/HelpArticle"));
const SetPassword = lazyWithRetry(() => import("./pages/auth/SetPassword"));
const AcceptInvite = lazyWithRetry(() => import("./pages/auth/AcceptInvite"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const EmailVerification = lazyWithRetry(() => import("./pages/EmailVerification"));
const DashboardHome = lazyWithRetry(() => import("./pages/DashboardHome"));
const DashboardEscrever = lazyWithRetry(() => import("./pages/DashboardEscrever"));
const DashboardPosts = lazyWithRetry(() => import("./pages/DashboardPosts"));
const DashboardIntegracoes = lazyWithRetry(() => import("./pages/DashboardIntegracoes"));
const DashboardNotaFiscal = lazyWithRetry(() => import("./pages/DashboardNotaFiscal"));
const GoogleOAuthCallback = lazyWithRetry(() => import("./pages/GoogleOAuthCallback"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

// Detect public blog routes (custom domains or /blog/*) so we can skip
// dashboard-only side effects (idle prefetch, theme provider, toasters, etc).
const isPublicRoute =
  typeof window !== "undefined" &&
  (isCustomDomain() || window.location.pathname.startsWith("/blog/"));

// Idle prefetch: warm up the heavy "Estratégia" chunks so that switching tabs
// inside that section is instant. Only runs in the dashboard — never on the
// public blog, where it would otherwise pull ~70 unrelated chunks and slow
// down LCP dramatically.
if (typeof window !== "undefined" && !isPublicRoute) {
  const prefetch = () => {
    // Páginas principais do painel — trocar de aba fica instantâneo.
    import("./pages/DashboardHome");
    import("./pages/DashboardMinhaMarca");
    import("./pages/DashboardBlog");
    import("./pages/DashboardPosts");
    import("./pages/DashboardIntegracoes");
    import("./pages/DashboardPlanejamento");
    import("./pages/DashboardPesquisa");
    import("./components/planejamento/EscritaTab");
    import("./components/automacoes/AprovacaoAgendamentoTab");
    import("./components/briefing/BriefingPalavrasChave");
    import("./components/planejamento/OportunidadesTab");
    import("./components/posts/PostsOportunidades");
  };
  const ric: any = (window as any).requestIdleCallback;
  if (typeof ric === "function") ric(prefetch, { timeout: 4000 });
  else setTimeout(prefetch, 2000);
}

const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

const MetaPixelRouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView();
  }, [location.pathname]);
  return null;
};

const AppRoutes = () => {
  if (isCustomDomain()) {
    return (
      <Routes>
        <Route path="/*" element={<CustomDomainRouter />} />
      </Routes>
    );
  }

  return (
    <Suspense fallback={<LazyFallback />}>
      {!isPublicRoute && <MetaPixelRouteTracker />}
      {!isPublicRoute && <GrowthFunnelTracker />}
      <Routes>
        <Route path="/" element={<FastblogLanding />} />
        <Route path="/planos" element={<FastblogPlanos />} />
        <Route path="/funcionalidades" element={<FastblogFuncionalidades />} />
        <Route path="/recursos" element={<FastblogRecursos />} />
        <Route path="/parcerias" element={<FastblogParcerias />} />
        <Route path="/termos-de-servico" element={<TermosServico />} />
        <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
        <Route path="/auth/verificar-email" element={<EmailVerification />} />
        <Route path="/auth/set-password" element={<SetPassword />} />
        <Route path="/auth/aceitar-convite" element={<AcceptInvite />} />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute requires="auth">
              <Onboarding />
            </ProtectedRoute>
          }
        />
        
        {/* ── Painel: nova estrutura de URLs limpas (sem /dashboard) ── */}
        <Route element={
          <ProtectedRoute requires="member">
            <DashboardLayoutRoute />
          </ProtectedRoute>
        }>
          {/* Início — substitui /dashboard, /dashboard/inicio, /dashboard/visao-geral, /dashboard/metricas, /dashboard/relatorios */}
          <Route path="/inicio" element={<DashboardHome />} />
          <Route path="/inicio/:tab" element={<DashboardHome />} />
          {/* Minha Marca */}
          <Route path="/minha-marca" element={<Navigate to="/minha-marca/meu-negocio" replace />} />
          <Route path="/minha-marca/:tab" element={<DashboardMinhaMarca />} />
          {/* Meu Blog */}
          <Route path="/meu-blog" element={<DashboardBlog />} />
          <Route path="/meu-blog/:tab" element={<DashboardBlog />} />
          {/* Posts */}
          <Route path="/posts" element={<DashboardPosts />} />
          <Route path="/posts/:tab" element={<DashboardPosts />} />
          <Route path="/posts/artigo/:id" element={<ArtigoView />} />
          {/* Estratégia */}
          <Route path="/estrategia" element={<DashboardPlanejamento />} />
          <Route path="/estrategia/:tab" element={<DashboardPlanejamento />} />
          {/* Pesquisa — agora vive como tab dentro de /estrategia */}
          <Route path="/pesquisa" element={<Navigate to="/estrategia/pesquisa" replace />} />
          <Route path="/pesquisa/:tab" element={<Navigate to="/estrategia/pesquisa" replace />} />
          {/* Newsletter foi descontinuada: links antigos voltam ao painel */}
          <Route path="/newsletter" element={<Navigate to="/" replace />} />
          <Route path="/newsletter/:tab" element={<Navigate to="/" replace />} />
          {/* Ebooks — em breve */}
          <Route path="/ebooks" element={<ComingSoon title="Ebooks" />} />
          {/* Integrações */}
          <Route path="/integracoes" element={<DashboardIntegracoes />} />
          {/* Nota Fiscal (add-on) */}
          <Route path="/nota-fiscal" element={<DashboardNotaFiscal />} />

          {/* Minha Conta */}
          <Route path="/minha-conta" element={<Navigate to="/minha-conta/perfil" replace />} />
          <Route path="/minha-conta/:tab" element={<DashboardConfiguracoes />} />
        </Route>

        {/* Dashboard routes - persistent layout (legado, com redirects) */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requires="member">
              <DashboardLayoutRoute />
            </ProtectedRoute>
          }
        >
          <Route index element={<LegacyRedirect to={() => "/inicio"} />} />
          <Route path="inicio" element={<LegacyRedirect to={() => "/inicio"} />} />
          <Route path="visao-geral" element={<LegacyRedirect to={() => "/inicio/visao-geral"} />} />
          <Route path="metricas" element={<LegacyRedirect to={() => "/inicio/analytics"} />} />
          <Route path="relatorios" element={<LegacyRedirect to={() => "/inicio/relatorios"} />} />
          <Route path="briefing" element={<Navigate to="/minha-marca" replace />} />
          <Route path="blog" element={<Navigate to="/meu-blog/aparencia" replace />} />
          <Route path="blog/preferencias" element={<Navigate to="/meu-blog/aparencia" replace />} />
          <Route path="blog/aparencia" element={<Navigate to="/meu-blog/aparencia" replace />} />
          <Route path="artigos" element={<Navigate to="/posts" replace />} />
          <Route path="artigos/:id" element={<LegacyRedirect to={({ params }) => `/posts/artigo/${params.id}`} />} />
          <Route path="artigo/:id" element={<LegacyRedirect to={({ params }) => `/posts/artigo/${params.id}`} />} />
          <Route path="blogs" element={<Navigate to="/meu-blog/aparencia" replace />} />
          <Route path="posts" element={<Navigate to="/posts" replace />} />
          <Route path="planejamento" element={
            <LegacyRedirect to={({ searchParams }) => {
              const t = searchParams.get("tab");
              return t ? `/estrategia/${t}` : "/estrategia";
            }} consumedKeys={["tab"]} />
          } />
          <Route path="pesquisa" element={
            <LegacyRedirect to={() => "/estrategia/pesquisa"} consumedKeys={["tab"]} />
          } />
          <Route path="minha-marca" element={
            <LegacyRedirect to={({ searchParams }) => {
              const t = searchParams.get("tab");
              return t ? `/minha-marca/${t}` : "/minha-marca";
            }} consumedKeys={["tab"]} />
          } />
          <Route path="newsletter" element={<Navigate to="/" replace />} />
          <Route path="ebooks" element={<Navigate to="/ebooks" replace />} />
          <Route path="empresa" element={<Navigate to="/minha-conta/empresa" replace />} />
          <Route path="financeiro" element={<Navigate to="/minha-conta/assinatura" replace />} />
          <Route path="configuracoes" element={
            <LegacyRedirect to={({ searchParams }) => {
              const t = searchParams.get("tab");
              return t ? `/minha-conta/${t}` : "/minha-conta/perfil";
            }} consumedKeys={["tab"]} />
          } />
          <Route path="assinatura" element={<Navigate to="/minha-conta/assinatura" replace />} />
          <Route path="equipe" element={<Navigate to="/minha-conta/equipe" replace />} />
          <Route path="integracoes" element={<Navigate to="/integracoes" replace />} />
        </Route>

        {/* Editor routes - sidebar collapsed */}
        <Route
          path="/posts/escrever"
          element={
            <ProtectedRoute requires="member">
              <DashboardLayoutRoute forceSidebarCollapsed />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardEscrever />} />
          <Route path=":id" element={<DashboardEscrever />} />
        </Route>

        {/* Editor legado: /dashboard/escrever[/:id] → /posts/escrever[/:id] */}
        <Route path="/dashboard/escrever" element={<LegacyRedirect to={() => "/posts/escrever"} />} />
        <Route path="/dashboard/escrever/:id" element={<LegacyRedirect to={({ params }) => `/posts/escrever/${params.id}`} />} />

        {/* Admin routes */}
        <Route path="/admin" element={<ProtectedRoute requires="admin"><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/clientes" element={<Navigate to="/admin?tab=clientes" replace />} />
        <Route path="/admin/clientes/novo" element={<ProtectedRoute requires="admin"><AdminNovoCliente /></ProtectedRoute>} />
        <Route path="/admin/clientes/:id" element={<ProtectedRoute requires="admin"><AdminClienteDetalhes /></ProtectedRoute>} />
        <Route path="/admin/clientes/:id/integracoes" element={<ProtectedRoute requires="admin"><AdminIntegracoes /></ProtectedRoute>} />
        <Route path="/admin/agente-ia" element={<ProtectedRoute requires="admin"><AdminAgenteIA /></ProtectedRoute>} />
        <Route path="/admin/uso-ia" element={<ProtectedRoute requires="admin"><AdminUsoIA /></ProtectedRoute>} />
        <Route path="/admin/planos" element={<ProtectedRoute requires="admin"><AdminPlanos /></ProtectedRoute>} />
        <Route path="/admin/configuracoes" element={<ProtectedRoute requires="admin"><AdminConfiguracoes /></ProtectedRoute>} />
        <Route path="/admin/checkout-boleto" element={<ProtectedRoute requires="admin"><AdminCheckoutBoleto /></ProtectedRoute>} />
        <Route path="/admin/nota-fiscal" element={<ProtectedRoute requires="admin"><AdminNotaFiscal /></ProtectedRoute>} />
        <Route path="/admin/saude" element={<ProtectedRoute requires="admin"><AdminSaude /></ProtectedRoute>} />
        <Route path="/admin/operacao" element={<ProtectedRoute requires="admin"><AdminOperacao /></ProtectedRoute>} />
        <Route path="/admin/auditoria" element={<ProtectedRoute requires="admin"><AdminAuditoria /></ProtectedRoute>} />
        <Route path="/admin/tracking" element={<ProtectedRoute requires="admin"><AdminTracking /></ProtectedRoute>} />
        <Route path="/admin/growth" element={<Navigate to="/admin?tab=funil" replace />} />
        <Route path="/admin/marketing" element={<ProtectedRoute requires="admin"><AdminMarketing /></ProtectedRoute>} />
        <Route path="/admin/performance" element={<ProtectedRoute requires="admin"><AdminPerformance /></ProtectedRoute>} />
        <Route path="/admin/funcoes" element={<ProtectedRoute requires="admin"><AdminFuncoes /></ProtectedRoute>} />
        <Route path="/admin/funcoes/:id" element={<ProtectedRoute requires="admin"><AdminFuncaoDetalhe /></ProtectedRoute>} />
        <Route path="/admin/base-conhecimento" element={<ProtectedRoute requires="admin"><AdminKnowledgeBase /></ProtectedRoute>} />
        <Route path="/admin/dev" element={<ProtectedRoute requires="admin"><AdminDev /></ProtectedRoute>} />
        <Route path="/admin/dev/newsletter/*" element={<Navigate to="/admin/dev" replace />} />
        <Route path="/checkout-boleto" element={<CheckoutBoleto />} />
        <Route path="/ajuda" element={<HelpCenter />} />
        <Route path="/ajuda/:slug" element={<HelpArticle />} />
        <Route path="/integrations/google/callback" element={<GoogleOAuthCallback />} />
        <Route path="/blog/:companySlug" element={<PublicBlog />} />
        <Route path="/blog/:companySlug/pagina/:page" element={<PublicBlog />} />
        <Route path="/blog/:companySlug/materiais" element={<PublicBlogMateriais />} />
        <Route path="/blog/:companySlug/sitemap.xml" element={<HostedBlogSitemap />} />
        <Route path="/blog/:companySlug/robots.txt" element={<HostedBlogSitemap kind="robots" />} />
        <Route path="/blog/:companySlug/llms.txt" element={<HostedBlogSitemap kind="llms" />} />
        <Route path="/blog/:companySlug/category/:categorySlug" element={<PublicBlogCategory />} />
        <Route path="/blog/:companySlug/category/:categorySlug/pagina/:page" element={<PublicBlogCategory />} />
        <Route path="/blog/:companySlug/:postSlug" element={<PublicBlogPost />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => {
  const content = (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          {!isPublicRoute && <Toaster />}
          {!isPublicRoute && <Sonner />}
          {/* v7_startTransition: a troca de rota vira uma transição do React —
              enquanto o pacote da próxima página baixa, a tela atual continua
              visível em vez do spinner/esqueleto do Suspense. */}
          <BrowserRouter future={{ v7_startTransition: true }}>
            <AppRoutes />
            {!isPublicRoute && <TrackingScriptsInjector />}
            {!isPublicRoute && <AuthDebugOverlay />}
            {!isPublicRoute && <CookieConsentBanner />}
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );

  if (isPublicRoute) return content;

  return (
    <ThemeProvider attribute="class" defaultTheme="light" forcedTheme="light" enableSystem={false}>
      {content}
    </ThemeProvider>
  );
};

export default App;
